import { API_CONFIG } from '../config/api';

export interface User {
  id: string;
  userName: string;
  email: string;
}

class UsersService {
  private usersCache: User[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  async getUsers(): Promise<User[]> {
    // Check if cache is still valid
    if (this.usersCache && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.usersCache;
    }

    const response = await fetch(`${API_CONFIG.baseUrl}/api/users`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const users = await response.json();
    this.usersCache = users;
    this.cacheTimestamp = Date.now();
    return users;
  }

  async getUserEmail(userId: string): Promise<string | null> {
    if (!userId) return null;
    
    try {
      const users = await this.getUsers();
      const user = users.find(u => u.id === userId);
      return user?.email || null;
    } catch (error) {
      console.error('Failed to resolve user email:', error);
      return null;
    }
  }

  async getUserByEmail(email: string): Promise<User | null> {
    if (!email) return null;
    
    try {
      const users = await this.getUsers();
      return users.find(u => u.email === email) || null;
    } catch (error) {
      console.error('Failed to find user by email:', error);
      return null;
    }
  }

  // Clear cache manually if needed
  clearCache(): void {
    this.usersCache = null;
    this.cacheTimestamp = 0;
  }
}

export const usersService = new UsersService();
